import React, { useState, useEffect, useMemo } from 'react';
import {
  availableModels as staticAvailableModels,
  dataTypes,
  gateTypes,
  snnExecutionModes,
  snnCompilationModes,
  aamlDomains,
  chainReasonerResolvers,
  chainReasonerModes,
  httpMethods,
  httpAuthTypes,
  httpBodyModes,
  httpResponseModes,
  crossLinkTargetTypes,
} from './utils/nodeTemplates';
import InputEditor from './editors/InputEditor';
import OutputEditor from './editors/OutputEditor';
import {
  LLM_MODEL_OPTIONS as STATIC_LLM_MODEL_OPTIONS,
  STAGE_MODEL_KEYS,
  MODEL_PRESETS,
  MLCF_CONTEXT_STRICTNESS_OPTIONS,
  CONTEXT_POTENCY_MODE_OPTIONS,
  PIPELINE_DEFAULTS,
  PIPELINE_RANGES,
} from '../../constants/pipelineSettings';
import { apiService, graphBuilderApi } from '../../services/api';
import './PropertiesPanel.css';

const PROVIDER_TYPE_LABELS = {
  openai: 'OpenAI',
  do_serverless: 'DO Serverless',
  do_droplet: 'DO Droplet',
  do_dedicated: 'DO Dedicated',
};

function PropertiesPanel({
  selectedElement,
  selectedType,
  processMetadata,
  gates,
  allNodes,
  availableGraphs = [],
  availableRouters = [],
  onMetadataUpdate,
  onNodeUpdate,
  onEdgeUpdate,
  onGateUpdate,
  onGateAdd,
  onGateDelete,
  onDeleteNode,
  onDeleteEdge,
  onSelectGate,
}) {
  const [activeTab, setActiveTab] = useState('properties');
  const [llmSettingsOpen, setLlmSettingsOpen] = useState(false);
  const [mlcfOpen, setMlcfOpen] = useState(false);
  const [crsOpen, setCrsOpen] = useState(false);
  const [serOpen, setSerOpen] = useState(false);
  const [platformTuningOpen, setPlatformTuningOpen] = useState(false);
  const [contextBundleOpen, setContextBundleOpen] = useState(false);
  const [queryHandlingOpen, setQueryHandlingOpen] = useState(false);
  const [llmProviders, setLlmProviders] = useState([]);
  const [workspaceSecrets, setWorkspaceSecrets] = useState([]);
  const [mcpServers, setMcpServers] = useState([]);
  const [availableProcesses, setAvailableProcesses] = useState([]);

  useEffect(() => {
    apiService.listLLMProviders().then((d) => setLlmProviders(d.providers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedElement || selectedType !== 'node') return;
    const t = selectedElement.type;
    if (t === 'httpNode' || t === 'pythonNode') {
      apiService.listWorkspaceSecrets().then((d) => setWorkspaceSecrets(d.secrets || [])).catch(() => {});
      if (t === 'pythonNode') {
        apiService.listMcpServers().then((d) => setMcpServers(d.servers || [])).catch(() => {});
      }
    }
    if (t === 'crossLinkNode') {
      graphBuilderApi
        .listProcessDefinitions({ status: 'active', limit: 1000 })
        .then((d) => setAvailableProcesses(d.processes || d || []))
        .catch(() => {});
    }
  }, [selectedElement, selectedType]);

  const availableModels = useMemo(() => {
    if (!llmProviders.length) return staticAvailableModels;
    const providerOpts = llmProviders.map((p) => ({
      value: p.model_name,
      label: `${p.model_name} (${PROVIDER_TYPE_LABELS[p.provider_type] || p.provider_type})`,
    }));
    const staticValues = new Set(staticAvailableModels.map((o) => o.value));
    const extra = providerOpts.filter((o) => !staticValues.has(o.value));
    return [...staticAvailableModels, ...extra];
  }, [llmProviders]);

  const LLM_MODEL_OPTIONS = useMemo(() => {
    if (!llmProviders.length) return STATIC_LLM_MODEL_OPTIONS;
    const providerOpts = llmProviders.map((p) => ({
      value: p.model_name,
      label: `${p.model_name} (${PROVIDER_TYPE_LABELS[p.provider_type] || p.provider_type})`,
    }));
    const staticValues = new Set(STATIC_LLM_MODEL_OPTIONS.map((o) => o.value));
    const extra = providerOpts.filter((o) => !staticValues.has(o.value));
    return [...STATIC_LLM_MODEL_OPTIONS, ...extra];
  }, [llmProviders]);

  // Render Process Metadata (when nothing is selected)
  const renderProcessProperties = () => (
    <div className="properties-section">
      <h3>Process Settings</h3>
      
      <div className="property-group">
        <label>Name</label>
        <input
          type="text"
          value={processMetadata.name || ''}
          onChange={(e) => onMetadataUpdate({ name: e.target.value })}
          placeholder="Process name"
        />
      </div>

      <div className="property-group">
        <label>Description</label>
        <textarea
          value={processMetadata.description || ''}
          onChange={(e) => onMetadataUpdate({ description: e.target.value })}
          placeholder="Process description"
          rows={3}
        />
      </div>

      <div className="property-row">
        <div className="property-group half">
          <label>Max Parallel</label>
          <input
            type="number"
            min={1}
            max={50}
            value={processMetadata.maxParallel || 5}
            onChange={(e) => onMetadataUpdate({ maxParallel: parseInt(e.target.value) })}
          />
        </div>
        <div className="property-group half">
          <label>Timeout (s)</label>
          <input
            type="number"
            min={60}
            max={7200}
            value={processMetadata.globalTimeoutSeconds || 600}
            onChange={(e) => onMetadataUpdate({ globalTimeoutSeconds: parseInt(e.target.value) })}
          />
        </div>
      </div>

      <div className="property-group">
        <label>Tags</label>
        <input
          type="text"
          value={(processMetadata.tags || []).join(', ')}
          onChange={(e) => onMetadataUpdate({ 
            tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) 
          })}
          placeholder="tag1, tag2, tag3"
        />
        <span className="help-text">Comma-separated tags</span>
      </div>

      <div className="property-group">
        <label>Focus Areas / Dynamic Goals (optional)</label>
        <textarea
          value={processMetadata.focusAreas || ''}
          onChange={(e) => onMetadataUpdate({ focusAreas: e.target.value })}
          placeholder={'One focus area per line, e.g.\nCOPD\nblood thinners'}
          rows={3}
        />
        <span className="help-text">Process-wide Spiking Episodic Memory (SEM) context, inherited by all SNN nodes. One per line or comma-separated. No-op unless SEM is enabled.</span>
      </div>

      {/* Gates Section */}
      <div className="properties-section gates-section">
        <div className="section-header">
          <h4>Gates</h4>
          <button 
            className="btn-small add"
            onClick={() => onGateAdd({
              gate_type: 'AND',
              input_nodes: [],
              output_nodes: [],
              description: ''
            })}
          >
            + Add Gate
          </button>
        </div>

        {gates.length === 0 ? (
          <p className="empty-message">No gates defined</p>
        ) : (
          <div className="gates-list">
            {gates.map((gate) => (
              <div 
                key={gate.gate_id} 
                className="gate-item"
                onClick={() => onSelectGate(gate)}
              >
                <span className="gate-type-badge">{gate.gate_type}</span>
                <span className="gate-id">{gate.gate_id}</span>
                <button 
                  className="btn-icon delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onGateDelete(gate.gate_id);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render LLM Node Properties
  const renderLLMNodeProperties = () => {
    const data = selectedElement.data;
    const update = (updates) => onNodeUpdate(selectedElement.id, updates);

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>LLM Node</h3>
          <button className="btn-small danger" onClick={onDeleteNode}>Delete</button>
        </div>

        <div className="property-group">
          <label>Node ID</label>
          <input
            type="text"
            value={data.node_id || selectedElement.id}
            onChange={(e) => update({ node_id: e.target.value })}
          />
        </div>

        <div className="property-group">
          <label>Name</label>
          <input
            type="text"
            value={data.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Node name"
          />
        </div>

        <div className="property-group">
          <label>Description</label>
          <textarea
            value={data.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Node description"
            rows={2}
          />
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.is_output || false}
              onChange={(e) => update({ is_output: e.target.checked })}
            />
            Output Node
          </label>
          <span className="help-text">
            Mark as a process output point. Its outputs become part of the execution's final response.
          </span>
        </div>

        <div className="property-group">
          <label>Model</label>
          <select
            value={data.model || 'gpt-4o'}
            onChange={(e) => update({ model: e.target.value })}
          >
            {availableModels.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>Temperature</label>
            <input
              type="number"
              min={0}
              max={2}
              step={0.1}
              value={data.temperature ?? 0.3}
              onChange={(e) => update({ temperature: parseFloat(e.target.value) })}
            />
          </div>
          <div className="property-group half">
            <label>Max Tokens</label>
            <input
              type="number"
              min={1}
              max={32000}
              value={data.max_tokens || 2048}
              onChange={(e) => update({ max_tokens: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div className="property-group">
          <label>System Prompt</label>
          <textarea
            value={data.system_prompt || ''}
            onChange={(e) => update({ system_prompt: e.target.value })}
            placeholder="System prompt for the LLM"
            rows={3}
          />
        </div>

        <div className="property-group">
          <label>Prompt Template</label>
          <textarea
            value={data.prompt_template || ''}
            onChange={(e) => update({ prompt_template: e.target.value })}
            placeholder="Use {{variable}} for inputs"
            rows={6}
            className="code-textarea"
          />
          <span className="help-text">Use {'{{variable_name}}'} for dynamic inputs</span>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>Timeout (s)</label>
            <input
              type="number"
              min={10}
              max={600}
              value={data.timeout_seconds || 120}
              onChange={(e) => update({ timeout_seconds: parseInt(e.target.value) })}
            />
          </div>
          <div className="property-group half">
            <label>Retry Count</label>
            <input
              type="number"
              min={0}
              max={5}
              value={data.retry_count ?? 2}
              onChange={(e) => update({ retry_count: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.expect_json || false}
              onChange={(e) => update({ expect_json: e.target.checked })}
            />
            Expect JSON Response
          </label>
        </div>

        {/* Inputs Section */}
        <InputEditor
          inputs={data.inputs || []}
          onChange={(inputs) => update({ inputs })}
        />

        {/* Outputs Section */}
        <OutputEditor
          outputs={data.outputs || []}
          onChange={(outputs) => update({ outputs })}
        />
      </div>
    );
  };

  // Render Platform Pipeline Node Properties
  const renderSNNNodeProperties = () => {
    const data = selectedElement.data;
    const snnConfig = data.snn_config || {};
    const update = (updates) => onNodeUpdate(selectedElement.id, updates);
    const updateSNN = (updates) => update({ snn_config: { ...snnConfig, ...updates } });

    const hasRouter = !!snnConfig.router_graph_id;
    const targetMode = hasRouter ? 'router' : 'graph';

    const handleTargetModeChange = (mode) => {
      if (mode === 'router') {
        updateSNN({ router_graph_id: '', router_graph_name: '', graph_id: '', graph_name: '' });
      } else {
        updateSNN({ router_graph_id: null, router_graph_name: null });
      }
    };

    const handleGraphSelect = (e) => {
      const gid = e.target.value;
      const g = availableGraphs.find(g => g.graphId === gid);
      updateSNN({ graph_id: gid, graph_name: g?.graphName || '', router_graph_id: null, router_graph_name: null });
    };

    const handleRouterSelect = (e) => {
      const rid = e.target.value;
      const r = availableRouters.find(r => r.graphId === rid);
      updateSNN({ router_graph_id: rid, router_graph_name: r?.graphName || '', graph_id: '', graph_name: '' });
    };

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>Platform Pipeline Node</h3>
          <button className="btn-small danger" onClick={onDeleteNode}>Delete</button>
        </div>

        <div className="property-group">
          <label>Node ID</label>
          <input
            type="text"
            value={data.node_id || selectedElement.id}
            onChange={(e) => update({ node_id: e.target.value })}
          />
        </div>

        <div className="property-group">
          <label>Name</label>
          <input
            type="text"
            value={data.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Node name"
          />
        </div>

        <div className="property-group">
          <label>Description</label>
          <textarea
            value={data.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            rows={2}
          />
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.is_output || false}
              onChange={(e) => update({ is_output: e.target.checked })}
            />
            Output Node
          </label>
          <span className="help-text">
            Mark as a process output point. Its outputs become part of the execution's final response.
          </span>
        </div>

        <h4 className="subsection-title">Target</h4>

        <div className="property-group">
          <div className="target-mode-toggle">
            <button
              className={`toggle-btn ${targetMode === 'graph' ? 'active' : ''}`}
              onClick={() => handleTargetModeChange('graph')}
            >
              Direct Graph
            </button>
            <button
              className={`toggle-btn ${targetMode === 'router' ? 'active' : ''}`}
              onClick={() => handleTargetModeChange('router')}
            >
              Router
            </button>
          </div>
        </div>

        {targetMode === 'graph' ? (
          <div className="property-group">
            <label>Graph</label>
            <select
              value={snnConfig.graph_id || ''}
              onChange={handleGraphSelect}
            >
              <option value="">-- Use process-level default --</option>
              {availableGraphs.map(g => (
                <option key={g.graphId} value={g.graphId}>
                  {g.graphName || g.graphId.substring(0, 8)}
                </option>
              ))}
            </select>
            {!snnConfig.graph_id && (
              <span className="help-text warning-text">
                No target selected. The process-level or runtime graph_id will be used.
              </span>
            )}
          </div>
        ) : (
          <>
            <div className="property-group">
              <label>Router Graph</label>
              <select
                value={snnConfig.router_graph_id || ''}
                onChange={handleRouterSelect}
              >
                <option value="">-- Select a router --</option>
                {availableRouters.map(r => (
                  <option key={r.graphId} value={r.graphId}>
                    {r.graphName || r.graphId.substring(0, 8)}
                  </option>
                ))}
              </select>
              {!snnConfig.router_graph_id && (
                <span className="help-text warning-text">
                  A router must be selected for this node to work.
                </span>
              )}
            </div>
            <div className="property-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={snnConfig.auto_resolve_ambiguous ?? true}
                  onChange={(e) => updateSNN({ auto_resolve_ambiguous: e.target.checked })}
                />
                Auto-resolve ambiguous routing
              </label>
              <span className="help-text">
                When checked, automatically uses the top-scoring target when routing is ambiguous
              </span>
            </div>
          </>
        )}

        <h4 className="subsection-title">Platform Configuration</h4>

        <div className="property-group">
          <label>Execution Mode</label>
          <select
            value={snnConfig.execution_mode || 'BUILD_AND_RUN'}
            onChange={(e) => updateSNN({ execution_mode: e.target.value })}
          >
            {snnExecutionModes.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="property-group">
          <label>Compilation Mode</label>
          <select
            value={snnConfig.snn_execution_mode || 'not_compiled'}
            onChange={(e) => updateSNN({ snn_execution_mode: e.target.value })}
          >
            {snnCompilationModes.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>Max Nodes</label>
            <input
              type="number"
              min={10}
              max={1000}
              value={snnConfig.max_nodes || 256}
              onChange={(e) => updateSNN({ max_nodes: parseInt(e.target.value) })}
            />
          </div>
          <div className="property-group half">
            <label>Max Hops</label>
            <input
              type="number"
              min={1}
              max={10}
              value={snnConfig.max_hops || 3}
              onChange={(e) => updateSNN({ max_hops: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>Similarity Top K</label>
            <input
              type="number"
              min={1}
              max={500}
              value={snnConfig.similarity_top_k || 150}
              onChange={(e) => updateSNN({ similarity_top_k: parseInt(e.target.value) })}
            />
          </div>
          <div className="property-group half">
            <label>Min Similarity</label>
            <input
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={snnConfig.min_similarity ?? 0.3}
              onChange={(e) => updateSNN({ min_similarity: parseFloat(e.target.value) })}
            />
          </div>
        </div>

        <div className="property-group">
          <label>NL Query Input</label>
          <input
            type="text"
            value={snnConfig.nl_query_input || 'nl_query'}
            onChange={(e) => updateSNN({ nl_query_input: e.target.value })}
          />
          <span className="help-text">Name of the input field containing the natural language query</span>
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={snnConfig.decompose_query ?? true}
              onChange={(e) => updateSNN({ decompose_query: e.target.checked })}
            />
            Decompose Query
          </label>
        </div>

        <div className="property-group">
          <label>Additional Context / Prompt (optional)</label>
          <textarea
            value={snnConfig.nl_prompt || ''}
            onChange={(e) => updateSNN({ nl_prompt: e.target.value })}
            placeholder="Optional: Provide additional context or instructions for the LLM response generation..."
            rows={3}
          />
          <span className="help-text">This text is prepended to conversation context and passed to the LLM</span>
        </div>

        <div className="property-group">
          <label>Focus Areas / Dynamic Goals (optional)</label>
          <textarea
            value={snnConfig.focus_areas || ''}
            onChange={(e) => updateSNN({ focus_areas: e.target.value })}
            placeholder={'One focus area per line, e.g.\nCOPD\nblood thinners'}
            rows={3}
          />
          <span className="help-text">Steers Spiking Episodic Memory (SEM) context activation for this node. One per line or comma-separated. No-op unless SEM is enabled.</span>
        </div>

        {renderPipelineSettings(snnConfig, updateSNN)}

        {/* Inputs Section */}
        <InputEditor
          inputs={data.inputs || []}
          onChange={(inputs) => update({ inputs })}
        />

        {/* Outputs Section */}
        <OutputEditor
          outputs={data.outputs || []}
          onChange={(outputs) => update({ outputs })}
        />
      </div>
    );
  };

  const renderPipelineSettings = (snnConfig, updateSNN) => {
    const ps = snnConfig.pipeline_settings || {};
    const updatePS = (updates) => updateSNN({
      pipeline_settings: { ...ps, ...updates }
    });
    const val = (key) => ps[key] ?? PIPELINE_DEFAULTS[key];
    const range = (key) => PIPELINE_RANGES[key] || {};
    const isCompiledMode = snnConfig.snn_execution_mode === 'compiled_full' || snnConfig.snn_execution_mode === 'compiled_full_numpy' || snnConfig.snn_execution_mode === 'compiled_sharded';

    return (
      <>
        {/* --- LLM Settings --- */}
        <div className="pipeline-collapsible">
          <div className="pipeline-collapsible-header" onClick={() => setLlmSettingsOpen(!llmSettingsOpen)}>
            <span className="settings-icon">{llmSettingsOpen ? '▼' : '▶'}</span>
            <span>LLM Settings</span>
          </div>
          {llmSettingsOpen && (
            <div className="pipeline-collapsible-body">
              <div className="property-group">
                <label>Default LLM Model</label>
                <select value={val('llm_model') || ''} onChange={(e) => updatePS({ llm_model: e.target.value || undefined })}>
                  <option value="">-- Pipeline default --</option>
                  {LLM_MODEL_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="property-group">
                <label>Model Preset</label>
                <div className="preset-buttons">
                  {Object.entries(MODEL_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      className={`btn-small ${(ps.model_preset || 'balanced') === key ? 'active' : ''}`}
                      onClick={() => {
                        const updates = { model_preset: key };
                        if (preset.models) updates.stage_models = preset.models;
                        updatePS(updates);
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              {(ps.model_preset === 'custom') && (
                <div className="stage-models-section">
                  {STAGE_MODEL_KEYS.map(stage => (
                    <div key={stage.key} className="property-group">
                      <label>{stage.label}</label>
                      <select
                        value={(ps.stage_models || {})[stage.key] || ''}
                        onChange={(e) => updatePS({ stage_models: { ...(ps.stage_models || {}), [stage.key]: e.target.value } })}
                      >
                        <option value="">-- Use default --</option>
                        {LLM_MODEL_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                      <span className="help-text">{stage.hint}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* --- MLCF Seed Filtering --- */}
        <div className="pipeline-collapsible">
          <div className="pipeline-collapsible-header" onClick={() => setMlcfOpen(!mlcfOpen)}>
            <span className="settings-icon">{mlcfOpen ? '▼' : '▶'}</span>
            <span>Seed Filtering (MLCF)</span>
          </div>
          {mlcfOpen && (
            <div className="pipeline-collapsible-body">
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('mlcf_enabled')} onChange={(e) => updatePS({ mlcf_enabled: e.target.checked })} />
                  Enable MLCF
                </label>
              </div>
              {val('mlcf_enabled') && (
                <>
                  <div className="filter-toggles">
                    {['identity', 'scope', 'constraint', 'context', 'structural'].map(f => (
                      <div key={f} className="property-group checkbox">
                        <label>
                          <input type="checkbox" checked={val(`mlcf_${f}_filter`)} onChange={(e) => updatePS({ [`mlcf_${f}_filter`]: e.target.checked })} />
                          {f.charAt(0).toUpperCase() + f.slice(1)} Filter
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="property-group">
                    <label>Identity Threshold</label>
                    <input type="range" min={range('mlcf_identity_threshold').min} max={range('mlcf_identity_threshold').max} step={range('mlcf_identity_threshold').step}
                      value={val('mlcf_identity_threshold')} onChange={(e) => updatePS({ mlcf_identity_threshold: parseFloat(e.target.value) })} />
                    <span className="help-text">{val('mlcf_identity_threshold').toFixed(2)}</span>
                  </div>
                  <div className="property-group">
                    <label>Scope Overlap Threshold</label>
                    <input type="range" min={range('mlcf_scope_overlap_threshold').min} max={range('mlcf_scope_overlap_threshold').max} step={range('mlcf_scope_overlap_threshold').step}
                      value={val('mlcf_scope_overlap_threshold')} onChange={(e) => updatePS({ mlcf_scope_overlap_threshold: parseFloat(e.target.value) })} />
                    <span className="help-text">{val('mlcf_scope_overlap_threshold').toFixed(2)}</span>
                  </div>
                  <div className="property-group">
                    <label>Context Strictness</label>
                    <select value={val('mlcf_context_strictness')} onChange={(e) => updatePS({ mlcf_context_strictness: e.target.value })}>
                      {MLCF_CONTEXT_STRICTNESS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="property-group">
                    <label>Min Survivors</label>
                    <input type="range" min={range('mlcf_min_survivors').min} max={range('mlcf_min_survivors').max} step={range('mlcf_min_survivors').step}
                      value={val('mlcf_min_survivors')} onChange={(e) => updatePS({ mlcf_min_survivors: parseInt(e.target.value) })} />
                    <span className="help-text">{val('mlcf_min_survivors')}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* --- CRS Synapse Activation --- */}
        <div className="pipeline-collapsible">
          <div className="pipeline-collapsible-header" onClick={() => setCrsOpen(!crsOpen)}>
            <span className="settings-icon">{crsOpen ? '▼' : '▶'}</span>
            <span>Connection Selection (CRS)</span>
          </div>
          {crsOpen && (
            <div className="pipeline-collapsible-body">
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('crs_enabled')} onChange={(e) => updatePS({ crs_enabled: e.target.checked })} />
                  Enable CRS
                </label>
              </div>
              {val('crs_enabled') && (
                <>
                  {[
                    { key: 'crs_semantic_weight', label: 'Semantic Weight' },
                    { key: 'crs_node_type_compat_weight', label: 'Node-Type Compat' },
                    { key: 'crs_intent_affinity_weight', label: 'Intent Affinity' },
                    { key: 'crs_scope_intersection_weight', label: 'Scope Intersection' },
                    { key: 'crs_causal_modality_weight', label: 'Causal / Modality' },
                    { key: 'crs_authority_weight', label: 'Authority' },
                    { key: 'crs_gate_readiness_weight', label: 'Gate Readiness' },
                    { key: 'crs_exception_alignment_weight', label: 'Exception Alignment' },
                  ].map(({ key, label }) => (
                    <div key={key} className="property-group">
                      <label>{label}</label>
                      <input type="range" min={range(key).min} max={range(key).max} step={range(key).step}
                        value={val(key)} onChange={(e) => updatePS({ [key]: parseFloat(e.target.value) })} />
                      <span className="help-text">{val(key).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="property-group">
                    <label>Evidence Threshold</label>
                    <input type="range" min={range('crs_evidence_threshold').min} max={range('crs_evidence_threshold').max} step={range('crs_evidence_threshold').step}
                      value={val('crs_evidence_threshold')} onChange={(e) => updatePS({ crs_evidence_threshold: parseFloat(e.target.value) })} />
                    <span className="help-text">{val('crs_evidence_threshold').toFixed(2)}</span>
                  </div>
                  <div className="property-group">
                    <label>Default Unscored Relevance</label>
                    <input type="range" min={range('crs_default_unscored_relevance').min} max={range('crs_default_unscored_relevance').max} step={range('crs_default_unscored_relevance').step}
                      value={val('crs_default_unscored_relevance')} onChange={(e) => updatePS({ crs_default_unscored_relevance: parseFloat(e.target.value) })} />
                    <span className="help-text">{val('crs_default_unscored_relevance').toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* --- Semantic Receptors --- */}
        <div className="pipeline-collapsible">
          <div className="pipeline-collapsible-header" onClick={() => setSerOpen(!serOpen)}>
            <span className="settings-icon">{serOpen ? '▼' : '▶'}</span>
            <span>Semantic Receptors</span>
          </div>
          {serOpen && (
            <div className="pipeline-collapsible-body">
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('enable_ser')} onChange={(e) => updatePS({ enable_ser: e.target.checked })} />
                  Enable SER Selection
                </label>
                <span className="help-text">Replaces Phase 4 + CRS with embedding-native receptor-space dot product. Requires SER artifacts.</span>
              </div>
              {val('enable_ser') && (
                <>
                  <div className="property-group">
                    <label>SER Temperature</label>
                    <input type="range" min={range('ser_temperature').min} max={range('ser_temperature').max} step={range('ser_temperature').step}
                      value={val('ser_temperature')} onChange={(e) => updatePS({ ser_temperature: parseFloat(e.target.value) })} />
                    <span className="help-text">{val('ser_temperature').toFixed(2)} | Distribution peakiness (lower = more focused)</span>
                  </div>
                  <div className="property-group">
                    <label>BM25 Additive Boost</label>
                    <input type="range" min={range('ser_bm25_boost').min} max={range('ser_bm25_boost').max} step={range('ser_bm25_boost').step}
                      value={val('ser_bm25_boost')} onChange={(e) => updatePS({ ser_bm25_boost: parseFloat(e.target.value) })} />
                    <span className="help-text">{val('ser_bm25_boost').toFixed(2)} | Additive BM25 keyword boost on top of SER selection</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* --- Platform Tuning (compiled modes only) --- */}
        {isCompiledMode && (
          <div className="pipeline-collapsible">
            <div className="pipeline-collapsible-header" onClick={() => setPlatformTuningOpen(!platformTuningOpen)}>
              <span className="settings-icon">{platformTuningOpen ? '▼' : '▶'}</span>
              <span>Platform Tuning</span>
            </div>
            {platformTuningOpen && (
              <div className="pipeline-collapsible-body">
                <h5 className="pipeline-sub-heading">Simulation</h5>
                {[
                  { key: 'simulate_duration_ms', label: 'Duration (ms)', parse: parseFloat },
                  { key: 'simulate_step_ms', label: 'Step (ms)', parse: parseFloat },
                  { key: 'max_recursive_ticks', label: 'Max Recursive Ticks', parse: parseInt },
                  { key: 'bias_hz', label: 'Bias Level', parse: parseFloat },
                  { key: 'gain', label: 'Gain', parse: parseFloat },
                  { key: 'min_background_rate_hz', label: 'Min Background Level', parse: parseFloat },
                ].map(({ key, label, parse }) => (
                  <div key={key} className="property-group">
                    <label>{label}</label>
                    <input type="number" min={range(key).min} max={range(key).max} step={range(key).step}
                      value={val(key)} onChange={(e) => updatePS({ [key]: parse(e.target.value) || PIPELINE_DEFAULTS[key] })} />
                  </div>
                ))}
                <h5 className="pipeline-sub-heading">Relevance Scoring</h5>
                {[
                  { key: 'rate_coding_min_hz', label: 'Min Measure', parse: parseFloat },
                  { key: 'rate_coding_max_hz', label: 'Max Measure', parse: parseFloat },
                  { key: 'rate_coding_base_hz', label: 'Base Measure', parse: parseFloat },
                  { key: 'softmax_temperature', label: 'Softmax Temperature', parse: parseFloat },
                ].map(({ key, label, parse }) => (
                  <div key={key} className="property-group">
                    <label>{label}</label>
                    <input type="number" min={range(key).min} max={range(key).max} step={range(key).step}
                      value={val(key)} onChange={(e) => updatePS({ [key]: parse(e.target.value) || PIPELINE_DEFAULTS[key] })} />
                  </div>
                ))}
                <h5 className="pipeline-sub-heading">Propagation</h5>
                <div className="property-group">
                  <label>Evidence Threshold</label>
                  <input type="range" min={range('propagation_evidence_threshold').min} max={range('propagation_evidence_threshold').max} step={range('propagation_evidence_threshold').step}
                    value={val('propagation_evidence_threshold')} onChange={(e) => updatePS({ propagation_evidence_threshold: parseFloat(e.target.value) })} />
                  <span className="help-text">{val('propagation_evidence_threshold').toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* --- Query Handling --- */}
        <div className="pipeline-collapsible">
          <div className="pipeline-collapsible-header" onClick={() => setQueryHandlingOpen(!queryHandlingOpen)}>
            <span className="settings-icon">{queryHandlingOpen ? '▼' : '▶'}</span>
            <span>Query Handling</span>
          </div>
          {queryHandlingOpen && (
            <div className="pipeline-collapsible-body">
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('enable_abstract_query_handler')} onChange={(e) => updatePS({ enable_abstract_query_handler: e.target.checked })} />
                  Enable AQH
                </label>
                <span className="help-text">Abstract Query Handler</span>
              </div>
              <div className="property-group">
                <label>Gap Fill Iterations</label>
                <input type="number" min={0} max={5}
                  value={val('max_gap_fill_iterations')}
                  onChange={(e) => updatePS({ max_gap_fill_iterations: parseInt(e.target.value) || 0 })}
                  disabled={!val('enable_abstract_query_handler')} />
              </div>
              <div className="property-group">
                <label>Traversal Depth</label>
                <input type="number" min={1} max={5}
                  value={val('graph_traversal_depth')}
                  onChange={(e) => updatePS({ graph_traversal_depth: parseInt(e.target.value) || 1 })}
                  disabled={!val('enable_abstract_query_handler')} />
              </div>
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('expand_with_vocabulary')} onChange={(e) => updatePS({ expand_with_vocabulary: e.target.checked })} />
                  Vocabulary Query Expansion
                </label>
                <span className="help-text">Expand query using graph vocabulary</span>
              </div>
            </div>
          )}
        </div>

        {/* --- Context Bundle --- */}
        <div className="pipeline-collapsible">
          <div className="pipeline-collapsible-header" onClick={() => setContextBundleOpen(!contextBundleOpen)}>
            <span className="settings-icon">{contextBundleOpen ? '▼' : '▶'}</span>
            <span>Context Bundle</span>
          </div>
          {contextBundleOpen && (
            <div className="pipeline-collapsible-body">
              <div className="property-group">
                <label>Context Potency Mode</label>
                <select
                  value={val('context_potency_mode') || 'auto'}
                  onChange={(e) => updatePS({ context_potency_mode: e.target.value })}
                >
                  {CONTEXT_POTENCY_MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="help-text">
                  Auto = backend picks per-query threshold (knee + p65 + top-K floor) and escalates if Judge flags low confidence
                </span>
              </div>
              {(val('context_potency_mode') || 'auto') === 'manual' && (
                <div className="property-group">
                  <label>Context Potency Threshold</label>
                  <input type="range" min={range('context_potency_threshold').min} max={range('context_potency_threshold').max} step={range('context_potency_threshold').step}
                    value={val('context_potency_threshold')} onChange={(e) => updatePS({ context_potency_threshold: parseFloat(e.target.value) })} />
                  <span className="help-text">{val('context_potency_threshold').toFixed(2)}</span>
                </div>
              )}
              {(val('context_potency_mode') || 'auto') === 'auto' && (
                <div className="property-group">
                  <label>Auto Potency Boost</label>
                  <input
                    type="range"
                    min={range('auto_potency_boost_multiplier').min}
                    max={range('auto_potency_boost_multiplier').max}
                    step={range('auto_potency_boost_multiplier').step}
                    value={val('auto_potency_boost_multiplier') ?? 1.3}
                    onChange={(e) => updatePS({ auto_potency_boost_multiplier: parseFloat(e.target.value) })}
                  />
                  <span className="help-text">
                    {(val('auto_potency_boost_multiplier') ?? 1.3).toFixed(1)}x — multiplier on the auto-picked threshold (raw * boost). Judge fallback strips back to raw.
                  </span>
                </div>
              )}
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('include_verbatim_nodes')} onChange={(e) => updatePS({ include_verbatim_nodes: e.target.checked })} />
                  Include Verbatim Nodes
                </label>
              </div>
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('enable_judge')} onChange={(e) => updatePS({ enable_judge: e.target.checked })} />
                  Enable Judge
                </label>
              </div>
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('enable_context_distillation')} onChange={(e) => updatePS({ enable_context_distillation: e.target.checked })} />
                  Enable Context Distillation
                </label>
              </div>
              <div className="property-group checkbox">
                <label>
                  <input type="checkbox" checked={val('enable_cross_encoder_rerank') ?? false} onChange={(e) => updatePS({ enable_cross_encoder_rerank: e.target.checked })} />
                  Cross-Encoder Rerank
                </label>
                <span className="help-text">Rerank candidates with cross-encoder model (slower, higher precision)</span>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  // Render Gate Node Properties
  const renderGateNodeProperties = () => {
    const data = selectedElement.data || selectedElement;
    const isCanvasGate = selectedType === 'node';
    const gateId = isCanvasGate ? selectedElement.id : data.gate_id;
    
    const update = isCanvasGate 
      ? (updates) => onNodeUpdate(selectedElement.id, updates)
      : (updates) => onGateUpdate(data.gate_id, updates);

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>Gate</h3>
          <button 
            className="btn-small danger" 
            onClick={() => isCanvasGate ? onDeleteNode() : onGateDelete(data.gate_id)}
          >
            Delete
          </button>
        </div>

        <div className="property-group">
          <label>Gate ID</label>
          <input
            type="text"
            value={gateId}
            readOnly
            className="readonly"
          />
        </div>

        <div className="property-group">
          <label>Gate Type</label>
          <select
            value={data.gate_type || 'AND'}
            onChange={(e) => update({ gate_type: e.target.value })}
          >
            {gateTypes.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>

        {data.gate_type === 'THRESHOLD' && (
          <div className="property-group">
            <label>Threshold</label>
            <input
              type="number"
              min={1}
              value={data.threshold || 2}
              onChange={(e) => update({ threshold: parseInt(e.target.value) })}
            />
            <span className="help-text">Number of inputs required to fire</span>
          </div>
        )}

        {data.gate_type === 'CONFIDENCE' && (
          <>
            <div className="property-group">
              <label>Confidence Threshold</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={data.confidence_threshold ?? 0.5}
                onChange={(e) => update({ confidence_threshold: parseFloat(e.target.value) })}
              />
              <span className="help-text">Min snn_score for high-confidence path (0-1)</span>
            </div>
            <div className="property-group">
              <label>Low-Confidence Nodes</label>
              <select
                multiple
                value={data.low_confidence_nodes || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, o => o.value);
                  update({ low_confidence_nodes: selected });
                }}
                style={{ minHeight: '80px' }}
              >
                {allNodes.map((node) => (
                  <option key={node.id} value={node.id}>{node.name || node.id}</option>
                ))}
              </select>
              <span className="help-text">Nodes activated when confidence is below threshold</span>
            </div>
          </>
        )}

        <div className="property-group">
          <label>Description</label>
          <textarea
            value={data.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Gate description"
            rows={2}
          />
        </div>

        <div className="property-group">
          <label>Input Nodes</label>
          <select
            multiple
            value={data.input_nodes || []}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => option.value);
              update({ input_nodes: values });
            }}
            className="multi-select"
          >
            {allNodes.map((node) => (
              <option key={node.id} value={node.id}>{node.name}</option>
            ))}
          </select>
          <span className="help-text">Ctrl+click to select multiple</span>
        </div>

        <div className="property-group">
          <label>Output Nodes</label>
          <select
            multiple
            value={data.output_nodes || []}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => option.value);
              update({ output_nodes: values });
            }}
            className="multi-select"
          >
            {allNodes.map((node) => (
              <option key={node.id} value={node.id}>{node.name}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // -------- Categorical Reasoner: Case Extractor --------
  const renderCaseExtractorNodeProperties = () => {
    const data = selectedElement.data || {};
    const cfg = data.case_extractor_config || {};
    const update = (updates) => onNodeUpdate(selectedElement.id, updates);
    const updateCfg = (patch) => update({
      case_extractor_config: { ...cfg, ...patch },
    });

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>Case Extractor</h3>
          <button className="btn-small danger" onClick={onDeleteNode}>Delete</button>
        </div>

        <div className="property-group">
          <label>Node ID</label>
          <input
            type="text"
            value={data.node_id || selectedElement.id}
            onChange={(e) => update({ node_id: e.target.value })}
          />
        </div>

        <div className="property-group">
          <label>Name</label>
          <input
            type="text"
            value={data.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Case Evidence Extractor"
          />
        </div>

        <div className="property-group">
          <label>Description</label>
          <textarea
            value={data.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            rows={2}
          />
        </div>

        <div className="property-group">
          <label>Schema path</label>
          <input
            type="text"
            value={cfg.schema_path || ''}
            onChange={(e) => updateCfg({ schema_path: e.target.value })}
            placeholder="GraphBuilder/backend/app/process_engine/case_evidence_schema.json"
          />
          <span className="help-text">Path to case_evidence_schema.json (relative to repo root).</span>
        </div>

        <div className="property-group">
          <label>LLM model</label>
          <select
            value={cfg.llm_model || 'gpt-4o'}
            onChange={(e) => updateCfg({ llm_model: e.target.value })}
          >
            {availableModels.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Temperature</label>
            <input
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={cfg.temperature ?? 0.0}
              onChange={(e) => updateCfg({ temperature: parseFloat(e.target.value) })}
            />
          </div>
          <div className="property-group">
            <label>Max tokens</label>
            <input
              type="number"
              min={512}
              step={256}
              value={cfg.max_tokens || 12000}
              onChange={(e) => updateCfg({ max_tokens: parseInt(e.target.value, 10) })}
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>case_text input name</label>
            <input
              type="text"
              value={cfg.case_text_input || 'case_text'}
              onChange={(e) => updateCfg({ case_text_input: e.target.value })}
            />
          </div>
          <div className="property-group">
            <label>domain input name</label>
            <input
              type="text"
              value={cfg.domain_input || 'domain'}
              onChange={(e) => updateCfg({ domain_input: e.target.value })}
            />
          </div>
        </div>

        <div className="property-group">
          <label>case_id input name</label>
          <input
            type="text"
            value={cfg.case_id_input || 'case_id'}
            onChange={(e) => updateCfg({ case_id_input: e.target.value })}
          />
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={cfg.cache_by_hash ?? true}
              onChange={(e) => updateCfg({ cache_by_hash: e.target.checked })}
            />
            Cache extraction by case_text hash
          </label>
          <span className="help-text">Re-using identical case text returns the cached frame; saves money during testing.</span>
        </div>

        <div className="property-group">
          <label>Timeout (seconds)</label>
          <input
            type="number"
            min={30}
            step={30}
            value={cfg.timeout_seconds || 480}
            onChange={(e) => updateCfg({ timeout_seconds: parseInt(e.target.value, 10) })}
          />
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.is_output || false}
              onChange={(e) => update({ is_output: e.target.checked })}
            />
            Output Node
          </label>
        </div>

        <div className="property-group">
          <label>Inputs</label>
          <InputEditor
            inputs={data.inputs || []}
            onChange={(inputs) => update({ inputs })}
            dataTypes={dataTypes}
          />
        </div>

        <div className="property-group">
          <label>Outputs</label>
          <OutputEditor
            outputs={data.outputs || []}
            onChange={(outputs) => update({ outputs })}
            dataTypes={dataTypes}
          />
        </div>
      </div>
    );
  };

  // -------- Categorical Reasoner: Chain Reasoner --------
  const renderChainReasonerNodeProperties = () => {
    const data = selectedElement.data || {};
    const cfg = data.chain_reasoner_config || {};
    const mode = cfg.mode || (cfg.pack_registry_path ? 'multi_pack' : 'single_pack');
    const filters = Array.isArray(cfg.domain_filter) ? cfg.domain_filter : [];
    const update = (updates) => onNodeUpdate(selectedElement.id, updates);
    const updateCfg = (patch) => update({
      chain_reasoner_config: { ...cfg, ...patch },
    });

    const toggleDomainFilter = (value) => {
      const current = new Set(filters);
      if (current.has(value)) {
        current.delete(value);
      } else {
        current.add(value);
      }
      updateCfg({ domain_filter: Array.from(current) });
    };

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>Chain Reasoner</h3>
          <button className="btn-small danger" onClick={onDeleteNode}>Delete</button>
        </div>

        <div className="property-group">
          <label>Node ID</label>
          <input
            type="text"
            value={data.node_id || selectedElement.id}
            onChange={(e) => update({ node_id: e.target.value })}
          />
        </div>

        <div className="property-group">
          <label>Name</label>
          <input
            type="text"
            value={data.name || ''}
            onChange={(e) => update({ name: e.target.value })}
          />
        </div>

        <div className="property-group">
          <label>Description</label>
          <textarea
            value={data.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            rows={2}
          />
        </div>

        <div className="property-group">
          <label>Mode</label>
          <select
            value={mode}
            onChange={(e) => updateCfg({ mode: e.target.value })}
          >
            {chainReasonerModes.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <span className="help-text">
            Multi-pack iterates every pack in pack_registry against the case evidence frame. Single-pack runs one pack only.
          </span>
        </div>

        {mode === 'multi_pack' ? (
          <>
            <div className="property-group">
              <label>Pack registry YAML</label>
              <input
                type="text"
                value={cfg.pack_registry_path || ''}
                onChange={(e) => updateCfg({ pack_registry_path: e.target.value })}
                placeholder="poc_snn_graph_nolm/reference_corpus/pack_registry.yaml"
              />
            </div>
            <div className="property-group">
              <label>Manifest JSON</label>
              <input
                type="text"
                value={cfg.manifest_path || ''}
                onChange={(e) => updateCfg({ manifest_path: e.target.value })}
                placeholder="poc_snn_graph_nolm/reference_corpus/manifest.json"
              />
            </div>
            <div className="property-group">
              <label>Decision policy YAML</label>
              <input
                type="text"
                value={cfg.decision_policy_path || ''}
                onChange={(e) => updateCfg({ decision_policy_path: e.target.value })}
                placeholder="poc_snn_graph_nolm/reference_corpus/decision_policy.yaml"
              />
            </div>
            <div className="property-group">
              <label>Domain filter</label>
              <div className="checkbox-list">
                {aamlDomains.map(d => (
                  <label key={d.value} className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={filters.includes(d.value)}
                      onChange={() => toggleDomainFilter(d.value)}
                    />
                    {d.label}
                  </label>
                ))}
              </div>
              <span className="help-text">Empty = run every domain in registry.</span>
            </div>
          </>
        ) : (
          <>
            <div className="property-group">
              <label>Pack YAML path</label>
              <input
                type="text"
                value={cfg.pack_path || ''}
                onChange={(e) => updateCfg({ pack_path: e.target.value })}
                placeholder="poc_snn_graph_nolm/reference_corpus/category_packs/<pack_id>.yaml"
              />
            </div>
            <div className="property-group">
              <label>Manifest JSON (optional)</label>
              <input
                type="text"
                value={cfg.manifest_path || ''}
                onChange={(e) => updateCfg({ manifest_path: e.target.value })}
                placeholder="poc_snn_graph_nolm/reference_corpus/manifest.json"
              />
            </div>
          </>
        )}

        <div className="property-group">
          <label>Resolver</label>
          <select
            value={cfg.resolver || 'snn_pipeline'}
            onChange={(e) => updateCfg({ resolver: e.target.value })}
          >
            {chainReasonerResolvers.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {cfg.resolver === 'snn_pipeline' && (
          <div className="property-group">
            <label>Pipeline run URL</label>
            <input
              type="text"
              value={cfg.pipeline_run_url || ''}
              onChange={(e) => updateCfg({ pipeline_run_url: e.target.value })}
              placeholder="http://127.0.0.1:8000/api/pipeline/run"
            />
          </div>
        )}

        <div className="property-row">
          <div className="property-group">
            <label>case_evidence input name</label>
            <input
              type="text"
              value={cfg.case_evidence_input || 'case_evidence_frame'}
              onChange={(e) => updateCfg({ case_evidence_input: e.target.value })}
            />
          </div>
          <div className="property-group">
            <label>case_text input name</label>
            <input
              type="text"
              value={cfg.case_text_input || 'case_text'}
              onChange={(e) => updateCfg({ case_text_input: e.target.value })}
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Max iterations / pack</label>
            <input
              type="number"
              min={1}
              max={1000}
              value={cfg.max_iterations_per_pack ?? 200}
              onChange={(e) => updateCfg({ max_iterations_per_pack: parseInt(e.target.value, 10) })}
            />
          </div>
          <div className="property-group">
            <label>Near-coverage delta</label>
            <input
              type="number"
              min={0}
              max={5}
              value={cfg.near_coverage_delta ?? 1}
              onChange={(e) => updateCfg({ near_coverage_delta: parseInt(e.target.value, 10) })}
            />
          </div>
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={cfg.require_at_least_one_case_predicate_per_chain ?? true}
              onChange={(e) => updateCfg({ require_at_least_one_case_predicate_per_chain: e.target.checked })}
            />
            Require at least one case predicate per chain
          </label>
        </div>

        <div className="property-group">
          <label>Timeout (seconds)</label>
          <input
            type="number"
            min={30}
            step={30}
            value={cfg.timeout_seconds || 720}
            onChange={(e) => updateCfg({ timeout_seconds: parseInt(e.target.value, 10) })}
          />
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.is_output || false}
              onChange={(e) => update({ is_output: e.target.checked })}
            />
            Output Node
          </label>
        </div>

        <div className="property-group">
          <label>Inputs</label>
          <InputEditor
            inputs={data.inputs || []}
            onChange={(inputs) => update({ inputs })}
            dataTypes={dataTypes}
          />
        </div>

        <div className="property-group">
          <label>Outputs</label>
          <OutputEditor
            outputs={data.outputs || []}
            onChange={(outputs) => update({ outputs })}
            dataTypes={dataTypes}
          />
        </div>
      </div>
    );
  };

  // Render Edge (Synapse) Properties
  const renderEdgeProperties = () => {
    const data = selectedElement.data || {};
    const update = (updates) => onEdgeUpdate(selectedElement.id, updates);

    // Get source and target node info
    const sourceNode = allNodes.find(n => n.id === selectedElement.source);
    const targetNode = allNodes.find(n => n.id === selectedElement.target);

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>Connection</h3>
          <button className="btn-small danger" onClick={onDeleteEdge}>Delete</button>
        </div>

        <div className="property-group">
          <label>Source Node</label>
          <input
            type="text"
            value={sourceNode?.name || selectedElement.source}
            readOnly
            className="readonly"
          />
        </div>

        <div className="property-group">
          <label>Target Node</label>
          <input
            type="text"
            value={targetNode?.name || selectedElement.target}
            readOnly
            className="readonly"
          />
        </div>

        <div className="property-group">
          <label>Condition</label>
          <input
            type="text"
            value={data.condition || ''}
            onChange={(e) => update({ condition: e.target.value })}
            placeholder="e.g., {{status}} == 'valid'"
          />
          <span className="help-text">Leave empty for unconditional flow</span>
        </div>

        <div className="property-group">
          <label>Weight</label>
          <input
            type="number"
            min={0}
            max={10}
            step={0.1}
            value={data.weight ?? 1.0}
            onChange={(e) => update({ weight: parseFloat(e.target.value) })}
          />
          <span className="help-text">Higher weight = higher priority when merging</span>
        </div>

        <div className="property-group">
          <label>Merge Strategy</label>
          <select
            value={data.mergeStrategy || 'replace'}
            onChange={(e) => update({ mergeStrategy: e.target.value })}
          >
            <option value="replace">Replace (highest weight wins)</option>
            <option value="append">Append (collect into list)</option>
            <option value="concat">Concat (join strings)</option>
            <option value="dict_merge">Dict Merge (shallow merge)</option>
          </select>
          <span className="help-text">How to combine when multiple edges target the same input</span>
        </div>

        <div className="property-group">
          <label>Output Mapping</label>
          <textarea
            value={JSON.stringify(data.outputMapping || {}, null, 2)}
            onChange={(e) => {
              try {
                const mapping = JSON.parse(e.target.value);
                update({ outputMapping: mapping });
              } catch (err) {
                // Invalid JSON, ignore
              }
            }}
            placeholder='{"source_output": "target_input"}'
            rows={4}
            className="code-textarea"
          />
          <span className="help-text">Maps source outputs to target inputs</span>
        </div>
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // Cross-Link Node Properties
  // ──────────────────────────────────────────────────────────────────
  const renderCrossLinkNodeProperties = () => {
    const data = selectedElement.data;
    const update = (updates) => onNodeUpdate(selectedElement.id, updates);
    const cfg = data.cross_link_config || {};
    const updateCfg = (patch) => update({ cross_link_config: { ...cfg, ...patch } });
    const targetType = cfg.target_type || 'process';

    const handleProcessSelect = (e) => {
      const pid = e.target.value;
      const p = availableProcesses.find((p) => p.process_id === pid);
      updateCfg({ target_process_id: pid, target_process_name: p?.name || '' });
    };
    const handleGraphSelect = (e) => {
      const gid = e.target.value;
      const g = availableGraphs.find((g) => g.graphId === gid);
      updateCfg({ graph_id: gid, graph_name: g?.graphName || '' });
    };
    const handleRouterSelect = (e) => {
      const rid = e.target.value;
      const r = availableRouters.find((r) => r.graphId === rid);
      updateCfg({ router_graph_id: rid, router_graph_name: r?.graphName || '' });
    };

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>Cross-Link Node</h3>
          <button className="btn-small danger" onClick={onDeleteNode}>Delete</button>
        </div>

        <div className="property-group">
          <label>Node ID</label>
          <input
            type="text"
            value={data.node_id || selectedElement.id}
            onChange={(e) => update({ node_id: e.target.value })}
          />
        </div>

        <div className="property-group">
          <label>Name</label>
          <input
            type="text"
            value={data.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Node name"
          />
        </div>

        <div className="property-group">
          <label>Description</label>
          <textarea
            value={data.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            rows={2}
          />
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.is_output || false}
              onChange={(e) => update({ is_output: e.target.checked })}
            />
            Output Node
          </label>
          <span className="help-text">
            Mark as a process output point. Its outputs become part of the execution's final response.
          </span>
        </div>

        <h4 className="subsection-title">Target</h4>

        <div className="property-group">
          <label>Target Type</label>
          <select
            value={targetType}
            onChange={(e) => updateCfg({ target_type: e.target.value })}
          >
            {crossLinkTargetTypes.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {targetType === 'process' && (
          <div className="property-group">
            <label>Process Map</label>
            <select value={cfg.target_process_id || ''} onChange={handleProcessSelect}>
              <option value="">-- Select a process --</option>
              {availableProcesses.map((p) => (
                <option key={p.process_id} value={p.process_id}>
                  {p.name || p.process_id.substring(0, 8)}
                </option>
              ))}
            </select>
            {!cfg.target_process_id && (
              <span className="help-text warning-text">
                A target process must be selected for this node to work.
              </span>
            )}
          </div>
        )}

        {targetType === 'graph' && (
          <div className="property-group">
            <label>Graph</label>
            <select value={cfg.graph_id || ''} onChange={handleGraphSelect}>
              <option value="">-- Use process-level default --</option>
              {availableGraphs.map((g) => (
                <option key={g.graphId} value={g.graphId}>
                  {g.graphName || g.graphId.substring(0, 8)}
                </option>
              ))}
            </select>
          </div>
        )}

        {targetType === 'router' && (
          <>
            <div className="property-group">
              <label>Router Graph</label>
              <select value={cfg.router_graph_id || ''} onChange={handleRouterSelect}>
                <option value="">-- Select a router --</option>
                {availableRouters.map((r) => (
                  <option key={r.graphId} value={r.graphId}>
                    {r.graphName || r.graphId.substring(0, 8)}
                  </option>
                ))}
              </select>
            </div>
            <div className="property-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={cfg.auto_resolve_ambiguous ?? true}
                  onChange={(e) => updateCfg({ auto_resolve_ambiguous: e.target.checked })}
                />
                Auto-resolve ambiguous routing
              </label>
            </div>
          </>
        )}

        {(targetType === 'graph' || targetType === 'router') && (
          <div className="property-group">
            <label>NL Query Input</label>
            <input
              type="text"
              value={cfg.nl_query_input || 'nl_query'}
              onChange={(e) => updateCfg({ nl_query_input: e.target.value })}
              placeholder="nl_query"
            />
            <span className="help-text">Name of the input field carrying the NL query.</span>
          </div>
        )}

        {targetType === 'process' && (
          <div className="property-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={cfg.propagate_workspace !== false}
                onChange={(e) => updateCfg({ propagate_workspace: e.target.checked })}
              />
              Propagate workspace to child
            </label>
            <span className="help-text">
              Pass the parent's workspace_id to the invoked process when it has none.
              The child inherits the parent's persistence mode automatically.
            </span>
          </div>
        )}

        <span className="help-text">
          Input/output mappings are optional. When omitted, the node's inputs are
          passed through to the target and the target's outputs are mapped back by
          matching output names.
        </span>

        {/* Inputs Section */}
        <InputEditor
          inputs={data.inputs || []}
          onChange={(inputs) => update({ inputs })}
        />

        {/* Outputs Section */}
        <OutputEditor
          outputs={data.outputs || []}
          onChange={(outputs) => update({ outputs })}
        />
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // HTTP Node Properties
  // ──────────────────────────────────────────────────────────────────
  const renderHttpNodeProperties = () => {
    const data = selectedElement.data;
    const update = (updates) => onNodeUpdate(selectedElement.id, updates);
    const cfg = data.http_config || {};
    const updateCfg = (patch) => update({ http_config: { ...cfg, ...patch } });

    const headersList = Object.entries(cfg.headers || {});
    const queryList = Object.entries(cfg.query_params || {});

    const setKeyValue = (currentMap, oldKey, newKey, newValue) => {
      const out = {};
      let inserted = false;
      Object.entries(currentMap || {}).forEach(([k, v]) => {
        if (k === oldKey) {
          if (newKey) {
            out[newKey] = newValue;
            inserted = true;
          }
        } else {
          out[k] = v;
        }
      });
      if (!inserted && oldKey === '' && newKey) {
        out[newKey] = newValue;
      }
      return out;
    };

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>HTTP Node</h3>
          <button className="btn-small danger" onClick={onDeleteNode}>Delete</button>
        </div>

        <div className="property-group">
          <label>Node ID</label>
          <input
            type="text"
            value={data.node_id || selectedElement.id}
            onChange={(e) => update({ node_id: e.target.value })}
          />
        </div>

        <div className="property-group">
          <label>Name</label>
          <input
            type="text"
            value={data.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Node name"
          />
        </div>

        <div className="property-group">
          <label>Description</label>
          <textarea
            value={data.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Node description"
            rows={2}
          />
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.is_output || false}
              onChange={(e) => update({ is_output: e.target.checked })}
            />
            Output Node
          </label>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>Method</label>
            <select
              value={cfg.method || 'GET'}
              onChange={(e) => updateCfg({ method: e.target.value })}
            >
              {httpMethods.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="property-group half">
            <label>Response Mode</label>
            <select
              value={cfg.response_mode || 'json'}
              onChange={(e) => updateCfg({ response_mode: e.target.value })}
            >
              {httpResponseModes.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="property-group">
          <label>URL</label>
          <input
            type="text"
            value={cfg.url || ''}
            onChange={(e) => updateCfg({ url: e.target.value })}
            placeholder="https://api.example.com/{{ resource_id }}"
          />
          <span className="help-text">Use {'{{variable_name}}'} for dynamic inputs.</span>
        </div>

        <div className="property-group">
          <label>Headers</label>
          {headersList.map(([k, v], idx) => (
            <div key={`h-${idx}-${k}`} className="kv-row">
              <input
                type="text"
                value={k}
                placeholder="Header name"
                onChange={(e) => updateCfg({ headers: setKeyValue(cfg.headers, k, e.target.value, v) })}
              />
              <input
                type="text"
                value={v}
                placeholder="Header value (Jinja2 ok)"
                onChange={(e) => updateCfg({ headers: setKeyValue(cfg.headers, k, k, e.target.value) })}
              />
              <button
                className="btn-small danger"
                onClick={() => updateCfg({ headers: setKeyValue(cfg.headers, k, '', '') })}
              >×</button>
            </div>
          ))}
          <button
            className="btn-small"
            onClick={() => updateCfg({ headers: { ...(cfg.headers || {}), '': '' } })}
          >+ Add Header</button>
        </div>

        <div className="property-group">
          <label>Query Parameters</label>
          {queryList.map(([k, v], idx) => (
            <div key={`q-${idx}-${k}`} className="kv-row">
              <input
                type="text"
                value={k}
                placeholder="Param name"
                onChange={(e) => updateCfg({ query_params: setKeyValue(cfg.query_params, k, e.target.value, v) })}
              />
              <input
                type="text"
                value={v}
                placeholder="Param value (Jinja2 ok)"
                onChange={(e) => updateCfg({ query_params: setKeyValue(cfg.query_params, k, k, e.target.value) })}
              />
              <button
                className="btn-small danger"
                onClick={() => updateCfg({ query_params: setKeyValue(cfg.query_params, k, '', '') })}
              >×</button>
            </div>
          ))}
          <button
            className="btn-small"
            onClick={() => updateCfg({ query_params: { ...(cfg.query_params || {}), '': '' } })}
          >+ Add Query Param</button>
        </div>

        <div className="property-group">
          <label>Body Mode</label>
          <select
            value={cfg.body_mode || 'none'}
            onChange={(e) => updateCfg({ body_mode: e.target.value })}
          >
            {httpBodyModes.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {cfg.body_mode === 'json' && (
          <div className="property-group">
            <label>JSON Body</label>
            <textarea
              className="code-textarea"
              rows={6}
              value={typeof cfg.body_json === 'string'
                ? cfg.body_json
                : JSON.stringify(cfg.body_json || {}, null, 2)}
              onChange={(e) => {
                const raw = e.target.value;
                try {
                  updateCfg({ body_json: JSON.parse(raw) });
                } catch {
                  updateCfg({ body_json: raw });
                }
              }}
              placeholder='{"key": "{{ value }}"}'
            />
            <span className="help-text">Values are Jinja2-templated against inputs.</span>
          </div>
        )}

        {cfg.body_mode === 'raw' && (
          <div className="property-group">
            <label>Raw Body</label>
            <textarea
              className="code-textarea"
              rows={6}
              value={cfg.body_raw || ''}
              onChange={(e) => updateCfg({ body_raw: e.target.value })}
              placeholder="Raw request body (Jinja2 ok)"
            />
          </div>
        )}

        {cfg.body_mode === 'form' && (
          <div className="property-group">
            <label>Form Fields (key=value per line)</label>
            <textarea
              className="code-textarea"
              rows={4}
              value={Object.entries(cfg.body_form || {}).map(([k, v]) => `${k}=${v}`).join('\n')}
              onChange={(e) => {
                const next = {};
                e.target.value.split('\n').forEach((line) => {
                  const idx = line.indexOf('=');
                  if (idx > 0) {
                    next[line.slice(0, idx).trim()] = line.slice(idx + 1);
                  }
                });
                updateCfg({ body_form: next });
              }}
              placeholder="key=value"
            />
          </div>
        )}

        <div className="property-group">
          <label>Authentication</label>
          <select
            value={cfg.auth_type || 'none'}
            onChange={(e) => updateCfg({ auth_type: e.target.value })}
          >
            {httpAuthTypes.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </div>

        {cfg.auth_type && cfg.auth_type !== 'none' && (
          <>
            <div className="property-group">
              <label>Credential (Workspace Secret)</label>
              <select
                value={cfg.auth_credential_ref || ''}
                onChange={(e) => updateCfg({ auth_credential_ref: e.target.value || null })}
              >
                <option value="">-- Select a workspace secret --</option>
                {workspaceSecrets.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
              {workspaceSecrets.length === 0 && (
                <span className="help-text">No secrets yet -- add one under Settings → Workspace Secrets.</span>
              )}
            </div>

            {(cfg.auth_type === 'header' || cfg.auth_type === 'api_key_query') && (
              <div className="property-group">
                <label>
                  {cfg.auth_type === 'header' ? 'Header Name' : 'Query Parameter Name'}
                </label>
                <input
                  type="text"
                  value={cfg.auth_param_name || ''}
                  onChange={(e) => updateCfg({ auth_param_name: e.target.value })}
                  placeholder={cfg.auth_type === 'header' ? 'X-API-Key' : 'api_key'}
                />
              </div>
            )}
          </>
        )}

        <div className="property-group">
          <label>Expected Status Codes (comma-separated)</label>
          <input
            type="text"
            value={(cfg.expected_status_codes || []).join(', ')}
            onChange={(e) => updateCfg({
              expected_status_codes: e.target.value
                .split(',')
                .map((s) => parseInt(s.trim(), 10))
                .filter((n) => Number.isFinite(n)),
            })}
            placeholder="200, 201, 202, 204"
          />
        </div>

        <div className="property-row">
          <div className="property-group half checkbox">
            <label>
              <input
                type="checkbox"
                checked={cfg.follow_redirects !== false}
                onChange={(e) => updateCfg({ follow_redirects: e.target.checked })}
              />
              Follow redirects
            </label>
          </div>
          <div className="property-group half checkbox">
            <label>
              <input
                type="checkbox"
                checked={cfg.verify_tls !== false}
                onChange={(e) => updateCfg({ verify_tls: e.target.checked })}
              />
              Verify TLS
            </label>
          </div>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>Timeout (s)</label>
            <input
              type="number"
              min={1}
              max={600}
              value={data.timeout_seconds || 30}
              onChange={(e) => update({ timeout_seconds: parseInt(e.target.value, 10) })}
            />
          </div>
          <div className="property-group half">
            <label>Retry Count</label>
            <input
              type="number"
              min={0}
              max={5}
              value={data.retry_count ?? 0}
              onChange={(e) => update({ retry_count: parseInt(e.target.value, 10) })}
            />
          </div>
        </div>

        <InputEditor inputs={data.inputs || []} onChange={(inputs) => update({ inputs })} />
        <OutputEditor outputs={data.outputs || []} onChange={(outputs) => update({ outputs })} />
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // Python Node Properties
  // ──────────────────────────────────────────────────────────────────
  const renderPythonNodeProperties = () => {
    const data = selectedElement.data;
    const update = (updates) => onNodeUpdate(selectedElement.id, updates);
    const cfg = data.python_config || {};
    const updateCfg = (patch) => update({ python_config: { ...cfg, ...patch } });

    const toggleMcp = (name) => {
      const current = new Set(cfg.mcp_servers || []);
      if (current.has(name)) current.delete(name);
      else current.add(name);
      updateCfg({ mcp_servers: Array.from(current) });
    };

    const codeStr = cfg.code || '';
    const isCortexActivation = codeStr.includes('cortex_activation') || codeStr.includes('run_expertise_activation');
    const linkedGraphId = cfg.linked_expertise_graph_id || null;
    const linkedGraphName = cfg.linked_expertise_graph_name || null;
    const matchedGraph = linkedGraphId ? availableGraphs.find(g => g.graphId === linkedGraphId) : null;

    return (
      <div className="properties-section">
        <div className="section-header">
          <h3>Python Node</h3>
          <button className="btn-small danger" onClick={onDeleteNode}>Delete</button>
        </div>

        <div className="property-group">
          <label>Node ID</label>
          <input
            type="text"
            value={data.node_id || selectedElement.id}
            onChange={(e) => update({ node_id: e.target.value })}
          />
        </div>

        <div className="property-group">
          <label>Name</label>
          <input
            type="text"
            value={data.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Node name"
          />
        </div>

        <div className="property-group">
          <label>Description</label>
          <textarea
            value={data.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Node description"
            rows={2}
          />
        </div>

        <div className="property-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.is_output || false}
              onChange={(e) => update({ is_output: e.target.checked })}
            />
            Output Node
          </label>
        </div>

        {isCortexActivation && (
          <div className="linked-expertise-section">
            <h4 className="subsection-title">🧠 Linked Expertise Graph</h4>
            <div className="property-group">
              <label>Expertise Graph</label>
              <select
                value={linkedGraphId || ''}
                onChange={(e) => {
                  const gid = e.target.value;
                  const g = availableGraphs.find(g => g.graphId === gid);
                  updateCfg({
                    linked_expertise_graph_id: gid || null,
                    linked_expertise_graph_name: g?.graphName || null,
                  });
                }}
              >
                <option value="">— Select expertise graph —</option>
                {availableGraphs.map(g => (
                  <option key={g.graphId} value={g.graphId}>{g.graphName}</option>
                ))}
              </select>
              <span className="help-text">
                The expertise graph activated by this cortex node at runtime.
              </span>
            </div>
            {linkedGraphId && (
              <div className="linked-graph-info">
                <div className="linked-graph-name">{matchedGraph?.graphName || linkedGraphName || linkedGraphId}</div>
                <a
                  className="btn-small primary"
                  href={`/graph-builder/${linkedGraphId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in Graph Builder ↗
                </a>
              </div>
            )}
          </div>
        )}

        <div className="property-group">
          <label>Entrypoint Function</label>
          <input
            type="text"
            value={cfg.entrypoint || 'main'}
            onChange={(e) => updateCfg({ entrypoint: e.target.value })}
            placeholder="main"
          />
          <span className="help-text">
            Called with a dict of inputs and must return a dict of outputs.
          </span>
        </div>

        <div className="property-group">
          <label>Python Code</label>
          <textarea
            className="code-textarea"
            rows={16}
            spellCheck={false}
            value={cfg.code || ''}
            onChange={(e) => updateCfg({ code: e.target.value })}
            placeholder="def main(inputs):&#10;    return {}"
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
          />
          <span className="help-text">
            Runs in an isolated subprocess. The bundled <code>cm_mcp</code> module
            is auto-injected for MCP server calls.
          </span>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>CPU Limit (s)</label>
            <input
              type="number"
              min={1}
              max={600}
              value={cfg.cpu_seconds ?? 30}
              onChange={(e) => updateCfg({ cpu_seconds: parseInt(e.target.value, 10) })}
            />
          </div>
          <div className="property-group half">
            <label>Wall Limit (s)</label>
            <input
              type="number"
              min={1}
              max={900}
              value={cfg.wall_seconds ?? 60}
              onChange={(e) => updateCfg({ wall_seconds: parseInt(e.target.value, 10) })}
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>Memory Limit (MB)</label>
            <input
              type="number"
              min={32}
              max={8192}
              value={cfg.memory_mb ?? 512}
              onChange={(e) => updateCfg({ memory_mb: parseInt(e.target.value, 10) })}
            />
          </div>
          <div className="property-group half checkbox">
            <label>
              <input
                type="checkbox"
                checked={cfg.network_access || false}
                onChange={(e) => updateCfg({ network_access: e.target.checked })}
              />
              Allow general network access
            </label>
          </div>
        </div>

        <div className="property-group">
          <label>Allowed MCP Servers</label>
          {mcpServers.length === 0 ? (
            <span className="help-text">
              No MCP servers configured -- add one under Settings → MCP Servers.
            </span>
          ) : (
            <div className="mcp-selector">
              {mcpServers.map((s) => (
                <label key={s.id} className="mcp-selector-row">
                  <input
                    type="checkbox"
                    checked={(cfg.mcp_servers || []).includes(s.name)}
                    onChange={() => toggleMcp(s.name)}
                  />
                  <span className="mcp-selector-name">{s.name}</span>
                  <span className="mcp-selector-transport">{s.transport}</span>
                </label>
              ))}
            </div>
          )}
          <span className="help-text">
            The sandbox can only reach servers you check here, regardless of network_access.
          </span>
        </div>

        <div className="property-row">
          <div className="property-group half">
            <label>Timeout (s)</label>
            <input
              type="number"
              min={1}
              max={900}
              value={data.timeout_seconds || 90}
              onChange={(e) => update({ timeout_seconds: parseInt(e.target.value, 10) })}
            />
          </div>
          <div className="property-group half">
            <label>Retry Count</label>
            <input
              type="number"
              min={0}
              max={5}
              value={data.retry_count ?? 0}
              onChange={(e) => update({ retry_count: parseInt(e.target.value, 10) })}
            />
          </div>
        </div>

        <InputEditor inputs={data.inputs || []} onChange={(inputs) => update({ inputs })} />
        <OutputEditor outputs={data.outputs || []} onChange={(outputs) => update({ outputs })} />
      </div>
    );
  };

  // Determine which properties to show
  const renderProperties = () => {
    if (!selectedElement) {
      return renderProcessProperties();
    }

    if (selectedType === 'edge') {
      return renderEdgeProperties();
    }

    if (selectedType === 'gate') {
      return renderGateNodeProperties();
    }

    if (selectedType === 'node') {
      const nodeType = selectedElement.type;
      if (nodeType === 'llmNode') {
        return renderLLMNodeProperties();
      }
      if (nodeType === 'snnPipelineNode') {
        return renderSNNNodeProperties();
      }
      if (nodeType === 'gateNode') {
        return renderGateNodeProperties();
      }
      if (nodeType === 'caseExtractorNode') {
        return renderCaseExtractorNodeProperties();
      }
      if (nodeType === 'chainReasonerNode') {
        return renderChainReasonerNodeProperties();
      }
      if (nodeType === 'httpNode') {
        return renderHttpNodeProperties();
      }
      if (nodeType === 'pythonNode') {
        return renderPythonNodeProperties();
      }
      if (nodeType === 'crossLinkNode') {
        return renderCrossLinkNodeProperties();
      }
    }

    return renderProcessProperties();
  };

  return (
    <div className="properties-panel">
      <div className="panel-header">
        <h2>Properties</h2>
        {selectedElement && (
          <span className="selected-type">
            {selectedType === 'node' ? selectedElement.type : selectedType}
          </span>
        )}
      </div>
      <div className="panel-content">
        {renderProperties()}
      </div>
    </div>
  );
}

export default PropertiesPanel;
